import React from 'react';

const TermsOfService = () => {
  const lastUpdated = 'January 15, 2025';

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Page header */}
      <div className="border-b border-slate-100 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-em to-teal flex items-center justify-center text-white text-xs font-bold">
              NT
            </div>
            <span className="font-semibold text-slate-800 hidden sm:block">Nova Terra</span>
          </div>
          <a href="/" className="text-sm text-em-dark hover:text-em-deeper font-medium transition-colors">
            ← Back to site
          </a>
        </div>
        <div className="h-px gradient-em" />
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero block */}
        <div className="mb-12 space-y-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-em-light border border-em-border text-xs text-em-deeper font-semibold">
            Legal
          </span>
          <h1 className="text-4xl font-bold text-slate-900 leading-tight">Terms of Service</h1>
          <p className="text-slate-400 text-sm">Last updated: {lastUpdated}</p>
          <p className="text-slate-500 leading-relaxed max-w-2xl">
            These Terms of Service (&quot;Terms&quot;) govern your access to and use of the Nova Terra Consulting website and any related services we provide. By using our site, you agree to these Terms.
          </p>
        </div>

        <div className="relative bg-white rounded-3xl border border-slate-100 shadow-card p-8 md:p-10 space-y-10 overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-0.5 gradient-em" />

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Services and Content</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Information on this site is for general informational purposes and does not constitute consulting advice. Any engagement with us will be governed by a separate written agreement that sets the specific scope, deliverables, and fees.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Acceptable Use</h2>
            <ul className="space-y-2.5">
              {[
                'Do not misuse the site, attempt to gain unauthorized access, or interfere with its normal operation.',
                'Do not upload or transmit content that is unlawful, harmful, or infringes the rights of others.',
                'Use the site only in compliance with applicable laws and these Terms.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-em flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Intellectual Property</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              The site, its content, and trademarks are owned by Nova Terra Consulting or our licensors and are protected by intellectual property laws. You may not copy, distribute, modify, or create derivative works without our prior written consent.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Confidentiality</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              If we exchange confidential information in connection with potential or active services, both parties will protect that information and use it only for the intended purpose, subject to any separate written confidentiality terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Third-Party Links</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Our site may reference third-party sites or tools. We do not control or endorse third-party content and are not responsible for their practices or policies.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Disclaimer of Warranties</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              The site is provided on an &quot;as is&quot; and &quot;as available&quot; basis. We disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Limitation of Liability</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              To the maximum extent permitted by law, Nova Terra Consulting is not liable for any indirect, incidental, consequential, special, or punitive damages, or for lost profits or revenues, arising from your use of the site.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Indemnification</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              You agree to indemnify and hold harmless Nova Terra Consulting from any claims, damages, or expenses arising from your misuse of the site or violation of these Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Changes to These Terms</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We may update these Terms from time to time. The &quot;Last updated&quot; date above reflects the most recent changes. Continued use of the site after changes become effective constitutes acceptance.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Governing Law</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              These Terms are governed by the laws of the State of California, without regard to conflict of law principles, unless a different jurisdiction is specified in a separate written agreement between us.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Contact Us</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Questions about these Terms can be sent to{' '}
              <a href="mailto:info@novaterraconsulting.net" className="text-em-dark hover:text-em-deeper font-medium transition-colors">
                info@novaterraconsulting.net
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <a href="/" className="text-sm text-em-dark hover:text-em-deeper font-medium transition-colors">
            ← Back to Nova Terra Consulting
          </a>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
